/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useEffect, useRef, useContext } from 'react';

import { MeContext } from 'blb-contexts/Me';
import { SubjectContext } from 'blb-contexts/Subject';
import { getPersona, isUnsignedESignNotificationPersona } from 'blb-persona/index';
import { UnsignedESignModal } from 'blb-persona/settlementAgent/components/UploadSignedClosingInterruptModal';
import { BlendBridge } from 'blb-types/blendBridge';

import { DocUploadModal } from '../DocUploadModal';
import { Task } from './Task';

export namespace DocUploadTask {
  export interface Props extends Omit<BlendBridge.API.UploadDocumentTask, 'type'> {
    footerText?: string;
    onSuccess: () => Promise<any>;
    prompt?: JSX.Element;
    taskPreOpeningSubheader?: string;
  }
}

export const DocUploadTask: React.FC<DocUploadTask.Props> = ({
  afterTask,
  uploadLimit,
  // multiple uploads is the default unless a Task specifically disables it
  allowMultipleUploads = true,
  buttonLabel = 'Upload',
  closingDesignation,
  disabledReason,
  displayType,
  documentType,
  isDisabled,
  onSuccess,
  prompt,
  sourceType,
  taskHeader,
  taskId,
  taskSubheader,
  footerText,
  taskPreOpeningSubheader,
  allowedFileExtensions,
}) => {
  const [shouldShowUnsignedModal, setShouldShowUnsignedModal] = useState<boolean>(false);
  const [isUnsignedModalOpen, setUnsignedModalOpen] = useState<boolean>(false);
  const [isDocUploadOpen, setDocUploadOpen] = useState<boolean>(false);

  const isMountedRef = useRef(true);
  const subject = useContext(SubjectContext);
  const me = useContext(MeContext);
  const persona = getPersona(me!.principalType);

  useEffect(() => {
    // If this useEffect is running, that means that this component is
    // mounted. Here we reset isMounted to true in case it was unmounted
    // previously after completing a task.
    isMountedRef.current = true;
    if (
      isUnsignedESignNotificationPersona(persona) &&
      persona.shouldShowUnsignedESignUIs(subject) &&
      persona.shouldUploadModalInterrupt(taskHeader)
    ) {
      setShouldShowUnsignedModal(true);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [subject]);

  const handleUnsignedModalClose = (hasAcknowledged: boolean) => {
    setUnsignedModalOpen(false);

    if (hasAcknowledged) {
      setShouldShowUnsignedModal(false);
      setDocUploadOpen(true);
    }
  };

  const handleModalOpen = () => {
    if (shouldShowUnsignedModal) {
      setUnsignedModalOpen(true);
      return;
    }

    setDocUploadOpen(true);
  };

  return (
    <Task
      buttonLabel={buttonLabel}
      disabledReason={disabledReason}
      displayType={displayType}
      isDisabled={isDisabled}
      onClick={handleModalOpen}
      taskHeader={taskHeader}
      taskId={taskId}
      taskPreOpeningSubheader={taskPreOpeningSubheader}
      type={BlendBridge.TaskTypes.UPLOAD_DOCUMENT}
    >
      {shouldShowUnsignedModal && (
        <UnsignedESignModal handleClose={handleUnsignedModalClose} isOpen={isUnsignedModalOpen} taskId={taskId} />
      )}

      <DocUploadModal
        afterTask={afterTask}
        allowMultipleUploads={allowMultipleUploads}
        allowedFileExtensions={allowedFileExtensions}
        closingDesignation={closingDesignation}
        description={prompt}
        documentType={documentType}
        footerText={footerText}
        handleClose={() => {
          // Uploading a document may lead to the the task being completed, resulting in this component being unmounted.
          // An error will occur if the state is changed after this component has been unmounted.
          // So, ensure the component is still mounted before taking action.
          if (isMountedRef.current) setDocUploadOpen(false);
        }}
        isOpen={isDocUploadOpen}
        onSuccess={onSuccess}
        sourceType={sourceType}
        subTitle={taskSubheader}
        taskId={taskId}
        title={taskHeader}
        uploadLimit={uploadLimit}
      />
    </Task>
  );
};
